import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";

const ORIGIN = window.location.origin;

const FAQS = [
  {
    q: "Where can I explore gold jewellery in Meerut?",
    a: "You can explore gold jewellery at our retail showroom at Abu Plaza, Abu Lane, Meerut, where our team presents collections across bridal sets, chains, bangles, rings, earrings, mangalsutras and coins. Jewellers and retailers can visit our flagship wholesale office in Sarafa Bazaar, Meerut. Book a showroom appointment and we prepare a selection around your occasion, budget and preferred weight before you arrive.",
  },
  {
    q: "Where is Ramesh Kapoor & Sons Jewellers located in Meerut?",
    a: "We operate from two Meerut locations: our flagship wholesale office in Sarafa Bazaar — the historic jewellery market of the city — and our retail showroom at Abu Plaza, Abu Lane. Our wholesale office for Delhi and the NCR region is located in Karol Bagh, New Delhi. Exact map locations and directions are available on our Visit Us section.",
  },
  {
    q: "Does Ramesh Kapoor & Sons Jewellers deal in wholesale jewellery?",
    a: "Yes. Wholesale is the principal strength of our jewellery business. We supply jewellers and retail houses from Sarafa Bazaar, Meerut and Karol Bagh, New Delhi, with stock across gold jewellery, diamond jewellery, bridal sets, chains, bangles, rings, bracelets, earrings, mangalsutras, pendants, coins and silver articles.",
  },
  {
    q: "Where can jewellery retailers enquire about wholesale jewellery in Uttar Pradesh?",
    a: "Jewellery retailers across Uttar Pradesh can enquire through our wholesale enquiry page, request the current catalogue with product codes and weight ranges, or book a trade meeting at our Sarafa Bazaar office in Meerut. Every enquiry goes directly to our wholesale desk.",
  },
  {
    q: "Does the company offer both gold and diamond jewellery?",
    a: "Yes. Our range spans gold jewellery in 22K and 18K, diamond jewellery including rings, necklaces, earrings, bracelets and pendants, traditional and contemporary designs, bridal collections, mangalsutras, gold and silver coins, and silver jewellery and articles.",
  },
  {
    q: "Where is the Delhi wholesale office?",
    a: "Our Delhi wholesale office is located in Karol Bagh, New Delhi, one of the capital's established jewellery trading areas. It serves jewellers and retailers across Delhi and the NCR region. Directions are available on our Visit Us section.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "JewelryStore",
      "@id": `${ORIGIN}/jewellery-meerut#business`,
      name: "Ramesh Kapoor and Sons Jewellers Pvt. Ltd.",
      description:
        "Jewellery house in Meerut operating across manufacturing, wholesale and retail — gold, diamond, bridal, traditional and contemporary jewellery, with wholesale offices in Sarafa Bazaar Meerut and Karol Bagh New Delhi, and a retail showroom at Abu Plaza Meerut.",
      url: `${ORIGIN}/jewellery-meerut`,
      parentOrganization: { "@type": "Organization", name: "RK Group", url: ORIGIN },
      address: [
        {
          "@type": "PostalAddress",
          streetAddress: "63, Jhanda Chowk, Sarafa Bazaar",
          addressLocality: "Meerut",
          addressRegion: "Uttar Pradesh",
          postalCode: "250002",
          addressCountry: "IN",
        },
        {
          "@type": "PostalAddress",
          streetAddress: "G-32 Abu Plaza, Abu Lane, Sadar Bazaar",
          addressLocality: "Meerut",
          addressRegion: "Uttar Pradesh",
          postalCode: "250001",
          addressCountry: "IN",
        },
        {
          "@type": "PostalAddress",
          streetAddress: "Building No. 2608, S-6, 2nd Floor, Bank Street, Karol Bagh",
          addressLocality: "New Delhi",
          addressRegion: "Delhi",
          postalCode: "110005",
          addressCountry: "IN",
        },
      ],
      hasMap: [
        "https://maps.app.goo.gl/MhhXC9oxBgBXoJVm7",
        "https://maps.app.goo.gl/vDPaKHht2JVENeYz8",
        "https://maps.app.goo.gl/wG2LD7ghkPFTzfn97",
      ],
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: ORIGIN },
        { "@type": "ListItem", position: 2, name: "Jewellery", item: `${ORIGIN}/jewellery` },
        { "@type": "ListItem", position: 3, name: "Jewellery in Meerut", item: `${ORIGIN}/jewellery-meerut` },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

const H2 = ({ children }) => (
  <h2 className="font-serif font-light text-3xl sm:text-4xl text-[#FBF9F5] tracking-tight">{children}</h2>
);

const H3 = ({ children }) => (
  <h3 className="font-serif text-xl sm:text-2xl text-[#C5A059]">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-sm sm:text-base text-[#94A3B8] leading-loose mt-5">{children}</p>
);

const Section = ({ id, eyebrow, title, children, bordered = true }) => (
  <section id={id} className={`${bordered ? "border-t border-white/10" : ""} py-16 sm:py-20`} data-testid={`seo-section-${id}`}>
    <Reveal>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <div className={eyebrow ? "mt-4" : ""}>{title}</div>
      {children}
    </Reveal>
  </section>
);

const InlineLink = ({ to, children }) => (
  <Link to={to} className="text-[#C5A059] underline decoration-[#C5A059]/40 underline-offset-4 hover:decoration-[#C5A059] transition-colors">
    {children}
  </Link>
);

export default function SeoJewelleryMeerut() {
  return (
    <div data-testid="jewellery-meerut-page" className="pt-28 sm:pt-36">
      <SEO
        title="Jewellers in Meerut | Gold, Diamond & Wholesale Jewellery | Ramesh Kapoor & Sons"
        description="Explore gold, diamond, bridal and wholesale jewellery with Ramesh Kapoor & Sons Jewellers Pvt. Ltd. in Meerut. Manufacturing, wholesale and retail jewellery expertise with locations in Meerut and Delhi."
        canonical={`${ORIGIN}/jewellery-meerut`}
        robots="index, follow"
        jsonLd={jsonLd}
      />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-[900px] mx-auto px-5 sm:px-8 text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
        <ol className="flex flex-wrap items-center gap-2">
          <li><Link to="/" className="hover:text-[#C5A059] transition-colors">Home</Link></li>
          <li className="text-[#C5A059]/60">·</li>
          <li><Link to="/jewellery" className="hover:text-[#C5A059] transition-colors">Jewellery</Link></li>
          <li className="text-[#C5A059]/60">·</li>
          <li className="text-[#C5A059]">Jewellery in Meerut</li>
        </ol>
      </nav>

      <header className="max-w-[900px] mx-auto px-5 sm:px-8 pt-10 pb-16">
        <Eyebrow>Ramesh Kapoor and Sons Jewellers Pvt. Ltd.</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4 leading-[1.05]">
          Jewellery in Meerut <span className="italic text-[#C5A059]">| Ramesh Kapoor &amp; Sons Jewellers Pvt. Ltd.</span>
        </h1>
        <P>
          Ramesh Kapoor &amp; Sons Jewellers Pvt. Ltd. is a jewellery house rooted in Meerut, Uttar Pradesh,
          working across the full life of a jewel — manufacturing and product development, wholesale supply to
          jewellers and retailers, and a retail showroom for families and individual buyers. Of these, wholesale
          is the principal strength of the business: from our flagship office in Sarafa Bazaar, Meerut, and our
          wholesale office in Karol Bagh, New Delhi, we supply the trade across Uttar Pradesh and the NCR region.
        </P>
        <P>
          Part of the RK Group — the family-led group founded and chaired by Mr. Ramesh Chand Kapoor — the
          jewellery company stands beside <InlineLink to="/real-estate">RK Builders</InlineLink>, the group's
          real-estate development business. This page brings together who we are, what we make and supply, and
          where to find us.
        </P>
      </header>

      <div className="max-w-[900px] mx-auto px-5 sm:px-8 pb-24">
        <div className="aspect-[16/7] overflow-hidden border border-white/10">
          <img
            src="/images/jewellery-bridal.jpg"
            alt="Bridal gold jewellery set — necklace, earrings and bangles from Ramesh Kapoor & Sons Jewellers, Meerut"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>

        <Section id="jewellery-in-meerut" eyebrow="The House" title={<H2>Jewellery in Meerut</H2>}>
          <P>
            Meerut has long been a jewellery city, and Sarafa Bazaar sits at its heart. Our work spans the
            categories families and jewellers ask for most: gold jewellery for daily wear and celebration,
            diamond jewellery for milestones, bridal collections built around weddings, and traditional designs
            that carry the region's taste, alongside contemporary pieces for a newer generation of buyers.
          </P>
          <P>
            The range covers chains, necklaces, bangles, bracelets, rings, earrings, pendants and mangalsutras,
            together with gold and silver coins, silver jewellery and silver articles. Whether you are a family
            choosing a single piece or a retailer stocking a counter, the same disciplines apply — verified
            purity, honest weighing, transparent making charges and designs chosen to endure.
          </P>
        </Section>

        <Section id="wholesale-jewellery" eyebrow="The Hero Business" title={<H2>Wholesale Jewellery in Meerut &amp; Uttar Pradesh</H2>}>
          <P>
            Wholesale is where this company is built. Jewellers and retail houses across Uttar Pradesh work with
            us because a wholesale relationship is really a stock relationship: the right designs, in the right
            weight ranges, available when the season demands them. Our Sarafa Bazaar office holds stock across
            gold jewellery, diamond jewellery, bridal sets, chains, bangles, rings, bracelets, earrings,
            mangalsutras, pendants, gold and silver coins, and silver products — organised by product code,
            purity and approximate weight so a retailer can enquire precisely and reorder without friction.
          </P>
          <P>
            Decades inside the Meerut jewellery market give us a working understanding of what sells in the
            region — which bridal weights move before the wedding season, which chains and bangles form the
            backbone of a retail counter, and how tastes differ between Meerut, western Uttar Pradesh and Delhi.
            That market understanding shapes what we develop and hold, and it is why retailers treat our office
            as a supply partner rather than a one-time seller.
          </P>
          <P>
            Our Karol Bagh office extends the same wholesale service to Delhi and the NCR. Trade buyers can{" "}
            <InlineLink to="/jewellery/catalogue">request the current catalogue</InlineLink>, filter stock by
            weight and purity, or{" "}
            <InlineLink to="/jewellery/wholesale">book a wholesale meeting or trade-account enquiry</InlineLink>{" "}
            at either office. Credit terms depend on the business relationship, order profile, payment history
            and internal approval.
          </P>
          <P>
            Wholesale buyers also work with us on continuity. New stock arrives as designs are developed and
            confirmed; fast-moving lines can be reordered by product code; and our team keeps track of what each
            counter has taken before, so repeat orders stay consistent in purity, weight and finish. For a
            retailer, that reliability is worth more than any single transaction — it is what keeps a display
            counter full through wedding seasons and festivals without last-minute sourcing.
          </P>
        </Section>

        <Section id="manufacturing" eyebrow="Craft" title={<H2>Jewellery Manufacturing in Meerut</H2>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 items-start">
            <div>
              <P>
                Jewellery is judged at the bench, not the counter. Our manufacturing and product development work
                centres on craftsmanship — clean casting and filing, secure stone setting, consistent polishing
                and finishing, and the kind of attention to detail that decides whether a piece feels ordinary
                or considered in the hand.
              </P>
              <P>
                New designs are developed against what jewellers actually report from their counters: wearable
                weights, durable construction, clasps and hinges that last, and finishing that survives daily
                use. It is a quiet discipline, refined across five decades of the family's work in the trade.
              </P>
            </div>
            <div className="aspect-[4/3] overflow-hidden border border-white/10">
              <img
                src="/images/jewellery-craft.jpg"
                alt="Goldsmith's hands crafting a gold bangle at a workbench — jewellery workmanship at Ramesh Kapoor & Sons, Meerut"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </Section>

        <Section id="gold-jewellery" eyebrow="Gold" title={<H2>Gold Jewellery in Meerut</H2>}>
          <P>
            Our gold jewellery range spans 22K (916) and 18K (750) work across chains, necklaces, bangles,
            bracelets, rings, earrings, pendants and mangalsutras, alongside 24K gold coins. Buyers in Meerut
            rightly compare more than design: the day's metal rate, net weight, purity, making charges and taxes
            together decide the final price of any piece.
          </P>
          <P>
            We publish the day's 24K, 22K and 18K gold rates openly — see{" "}
            <InlineLink to="/jewellery/rates">today's gold rate in Meerut</InlineLink> — and every finished price
            is confirmed at the counter with each component disclosed. Browse by category in our{" "}
            <InlineLink to="/jewellery">jewellery collections</InlineLink>, including{" "}
            <InlineLink to="/jewellery/category/gold-jewellery">gold jewellery</InlineLink>,{" "}
            <InlineLink to="/jewellery/category/chains">chains</InlineLink>,{" "}
            <InlineLink to="/jewellery/category/bangles">bangles</InlineLink> and{" "}
            <InlineLink to="/jewellery/category/mangalsutras">mangalsutras</InlineLink>.
          </P>
          <P>
            When comparing gold jewellery shops in Meerut, it helps to ask four questions of any piece: What is
            the day's metal rate? What is the net weight? What purity is stamped on it? And what are the making
            charges? Our team answers all four at the counter before any purchase is finalised — and the same
            openness applies whether the buyer is a family or a fellow jeweller.
          </P>
        </Section>

        <Section id="diamond-jewellery" eyebrow="Diamonds" title={<H2>Diamond Jewellery in Meerut</H2>}>
          <P>
            Our diamond jewellery covers rings, necklaces, earrings, bracelets and pendants, from restrained
            daily-wear pieces to occasion sets. Diamond pricing is explained the same way as gold — the value of
            the stones, the metal, the making and applicable taxes are set out separately, so a buyer always
            knows what they are paying for. Explore{" "}
            <InlineLink to="/jewellery/category/diamond-jewellery">diamond jewellery</InlineLink>,{" "}
            <InlineLink to="/jewellery/category/rings">rings</InlineLink> and{" "}
            <InlineLink to="/jewellery/category/earrings">earrings</InlineLink> in the collections.
          </P>
          <P>
            Every diamond piece is presented with its stone information explained in plain terms, so buyers
            comparing diamond jewellery in Meerut can weigh design, stone quality and price with full clarity
            before deciding.
          </P>
        </Section>

        <Section id="bridal-jewellery" eyebrow="Weddings" title={<H2>Bridal &amp; Wedding Jewellery in Meerut</H2>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 items-start">
            <div className="aspect-[4/3] overflow-hidden border border-white/10 order-2 sm:order-1">
              <img
                src="/images/showroom.jpg"
                alt="The Abu Plaza jewellery showroom interior in Meerut — bridal and wedding jewellery consultations"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="order-1 sm:order-2">
              <P>
                A wedding purchase in Meerut is rarely a single piece — it is a set built over several visits,
                across budgets and generations of the family. Our bridal work covers bridal gold sets, diamond
                sets, mangalsutras, bangles and the accompanying pieces a wedding household expects, in both
                traditional and contemporary styles.
              </P>
              <P>
                Families can{" "}
                <InlineLink to="/jewellery/appointment">book a showroom appointment</InlineLink> at Abu Plaza and
                tell us the occasion, budget and preferred weight range in advance; our team prepares a
                selection before the visit, so time at the counter is spent choosing, not searching.
              </P>
              <P>
                Beyond the wedding day, the showroom also serves engagement, anniversary, festival and gifting
                purchases — including gold and silver coins for auspicious occasions, and men's jewellery across
                chains, rings and bracelets.
              </P>
            </div>
          </div>
        </Section>

        <Section id="value" eyebrow="Fair Value" title={<H2>Competitive Jewellery Prices in Meerut</H2>}>
          <P>
            Customers searching for affordable jewellery in Meerut, competitive jewellery prices or even the
            cheapest jewellery options in Meerut often compare not only price, but also purity, craftsmanship,
            design, transparency and long-term trust. Ramesh Kapoor &amp; Sons Jewellers Pvt. Ltd. focuses on
            bringing together jewellery expertise, extensive variety and dependable relationships across its
            wholesale and retail operations.
          </P>
          <P>
            Because wholesale is our principal business, retail customers benefit from the same sourcing,
            workmanship and stock depth that jewellers rely on. We would rather earn a comparison than a slogan.
          </P>
        </Section>

        <Section id="locations" eyebrow="Visit" title={<H2>Our Jewellery Locations</H2>}>
          <P>
            Three addresses carry the business. Full maps and directions are in the{" "}
            <InlineLink to="/#visit-us">Visit Us section</InlineLink> and on our{" "}
            <InlineLink to="/contact">contact page</InlineLink>.
          </P>
          <div className="mt-10 space-y-0">
            {[
              {
                name: "Sarafa Bazaar, Meerut",
                fn: "Flagship Wholesale & Jewellery Operations",
                address: "63, Jhanda Chowk, Sarafa Bazaar, Meerut, Uttar Pradesh 250002",
                map: "https://maps.app.goo.gl/MhhXC9oxBgBXoJVm7?g_st=ic",
              },
              {
                name: "Abu Plaza, Abu Lane, Meerut",
                fn: "Retail Showroom",
                address: "G-32 Abu Plaza, Abu Lane, Sadar Bazaar, Meerut, Uttar Pradesh 250001",
                map: "https://maps.app.goo.gl/vDPaKHht2JVENeYz8?g_st=ic",
              },
              {
                name: "Karol Bagh, New Delhi",
                fn: "Wholesale Office",
                address: "Building No. 2608, S-6, 2nd Floor, Bank Street, Karol Bagh, New Delhi 110005",
                map: "https://maps.app.goo.gl/wG2LD7ghkPFTzfn97?g_st=ic",
              },
            ].map((l, i) => (
              <div key={l.name} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8 py-6 border-t border-white/10">
                <span className="font-serif text-xl text-[#C5A059]/50 w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <H3>{l.name}</H3>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1 font-semibold">{l.fn}</p>
                  <p className="text-sm text-[#94A3B8] mt-2">{l.address}</p>
                </div>
                <a href={l.map} target="_blank" rel="noreferrer"
                  className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C5A059] border-b border-[#C5A059]/40 pb-1 hover:border-[#C5A059] transition-colors shrink-0">
                  Get Directions →
                </a>
              </div>
            ))}
          </div>
        </Section>

        <Section id="faq" eyebrow="Questions" title={<H2>Frequently Asked Questions</H2>}>
          <div className="mt-10 space-y-0">
            {FAQS.map((f, i) => (
              <details key={i} className="group border-t border-white/10 py-6" data-testid={`faq-${i}`}>
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-300">
                    {f.q}
                  </h3>
                  <span className="text-[#C5A059] text-2xl leading-none mt-1 transition-transform duration-300 group-open:rotate-45">+</span>
                </summary>
                <p className="text-sm sm:text-base text-[#94A3B8] leading-loose mt-4 max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        <section className="border-t border-white/10 pt-16 pb-4 text-center">
          <Reveal>
            <h2 className="font-serif font-light text-2xl sm:text-4xl text-[#FBF9F5]">
              Trade or family — <span className="italic text-[#C5A059]">begin the conversation.</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/jewellery/wholesale" data-testid="seo-wholesale-cta"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#C5A059] text-[#060A12] px-8 py-4 hover:bg-[#d6b678] transition-colors duration-300">
                Wholesale Enquiries
              </Link>
              <Link to="/jewellery/appointment" data-testid="seo-appointment-cta"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-white/20 text-[#FBF9F5] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                Book a Showroom Appointment
              </Link>
              <Link to="/jewellery/rates" data-testid="seo-rates-cta"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#94A3B8] px-8 py-4 hover:text-[#C5A059] transition-colors duration-300">
                Today's Gold Rate
              </Link>
            </div>
          </Reveal>
        </section>
      </div>
    </div>
  );
}
